import type { AxiosInstance } from 'axios'

/**
 * Holding the JWT pair in memory and sending it as headers, rather than relying
 * on the cookies the server also sets.
 *
 * When embedded in the webclient our bundle runs on the *embedding* origin
 * (e.g. http://dev.sendhub.com) while this app's API is HTTPS on another
 * (https://integrations-dev.sendhub.com). Schemeful Same-Site counts a scheme
 * difference as cross-site, so the cookie pair is third-party there and subject
 * to third-party cookie policy — and a `Secure` cookie is not even readable
 * from JS on a plain-HTTP page, which silently breaks the refresh path.
 *
 * /auth/sso and /auth/refresh both return the pair in their response body, so
 * we keep it here and send it explicitly. The server accepts either spelling
 * (see getAccessTokenFromRequest in auth-middleware.ts), so same-origin and
 * prod callers are unaffected — the cookies still work there.
 *
 * Memory, not localStorage: LoginPage re-runs doSso on every load when embedded,
 * so a page refresh re-mints the pair anyway, and nothing durable holds a
 * credential an XSS could read.
 */
export const REFRESH_TOKEN_HEADER = 'X-Refresh-Token'

let accessToken: string | undefined
let refreshToken: string | undefined

export function setTokens(tokens: { authToken?: string; refreshToken?: string }): void {
  if (tokens.authToken) accessToken = tokens.authToken
  if (tokens.refreshToken) refreshToken = tokens.refreshToken
}

export function getAccessToken(): string | undefined {
  return accessToken
}

export function getRefreshToken(): string | undefined {
  return refreshToken
}

export function clearTokens(): void {
  accessToken = undefined
  refreshToken = undefined
}

/** Header bag for `fetch` callers; empty when no token is held. */
export function bearerTokenHeader(): Record<string, string> {
  return accessToken ? { Authorization: `Bearer ${accessToken}` } : {}
}

/**
 * Attach the access token to every request an axios instance makes.
 *
 * A request that already carries its own Authorization is left alone: some
 * operations authenticate with a different credential entirely (e.g. the Zapier
 * access token on /zapier/zaps), and silently overwriting it would swap in a
 * token the upstream doesn't accept.
 */
export function attachBearerToken(instance: AxiosInstance): void {
  instance.interceptors.request.use((requestConfig) => {
    if (accessToken && !requestConfig.headers.get('Authorization')) requestConfig.headers.set('Authorization', `Bearer ${accessToken}`)
    return requestConfig
  })
}
